/**
 * MOONOCASE page motion — boot splash, prefetch, soft AJAX grid.
 * No top progress bar (it fought with AJAX filters / square clicks).
 */
(function () {
  const BOOT_KEY = 'ac-boot-seen';
  const MIN_BOOT_MS = 520;
  const bootStarted = performance.now();
  const prefetched = new Set();

  const reduceMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function dismissBoot() {
    const boot = document.getElementById('AcBoot');
    document.documentElement.classList.remove('ac-booting');
    document.documentElement.classList.add('ac-booted');

    try {
      sessionStorage.setItem(BOOT_KEY, '1');
    } catch (e) {}

    if (!boot || boot.classList.contains('is-done')) return;

    boot.classList.add('is-done');
    boot.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      boot.remove();
    }, 700);
  }

  function scheduleBootDismiss() {
    if (document.documentElement.classList.contains('ac-boot-skip')) {
      dismissBoot();
      return;
    }

    const finish = () => {
      const elapsed = performance.now() - bootStarted;
      const wait = Math.max(0, MIN_BOOT_MS - elapsed);
      window.setTimeout(dismissBoot, reduceMotion() ? 0 : wait);
    };

    /* Don't wait for window.load (images/fonts) — DOM is enough to navigate. */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', finish, { once: true });
    } else {
      finish();
    }

    window.setTimeout(dismissBoot, 2500);
  }

  function isInternalHref(href, anchor) {
    if (!anchor || !(anchor instanceof HTMLAnchorElement)) return false;
    if (anchor.target && anchor.target !== '' && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download')) return false;
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return false;
    if (
      anchor.hasAttribute('data-collection-filter-url') ||
      anchor.hasAttribute('data-collection-filter-clear') ||
      anchor.closest(
        '.auracase-collections, .facets-container, facet-filters-form, .mobile-facets, .active-facets'
      )
    ) {
      return false;
    }

    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (e) {
      return false;
    }
    if (url.origin !== window.location.origin) return false;
    if (url.pathname === '/cart/change') return false;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return false;
    return true;
  }

  function isInternalNavAnchor(anchor) {
    if (!isInternalHref(anchor && anchor.getAttribute('href'), anchor)) return false;
    /* Exclude cart/search modals, but allow menu-drawer (mobile nav). */
    if (anchor.closest('cart-drawer, predictive-search, details-modal')) return false;
    /* Pattern circles swap the case image themselves. A full-document
       prefetch here competes with that image and the product HTML. */
    if (anchor.closest('ac-pattern-picker')) return false;
    return true;
  }

  function prefetchUrl(href) {
    if (!href || prefetched.has(href)) return;
    if (!document.head) return;
    prefetched.add(href);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function onPointerIntent(event) {
    const anchor = event.target.closest?.('a[href]');
    if (!isInternalNavAnchor(anchor)) return;
    prefetchUrl(anchor.href);
  }

  /** Soft product-grid swap for Dawn facets (sort / non-path filters). */
  window.acSoftGridSwap = function acSoftGridSwap(mutate) {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid || reduceMotion()) {
      mutate();
      return Promise.resolve();
    }

    // Clear loading dim before swap so we don't stack opacity hits
    grid.querySelector('.collection.loading')?.classList.remove('loading');
    grid.classList.remove('ac-grid-fading', 'ac-grid-enter');

    mutate();

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        grid.classList.add('ac-grid-enter');
        window.setTimeout(() => {
          grid.classList.remove('ac-grid-enter');
          resolve();
        }, 420);
      });
    });
  };

  scheduleBootDismiss();

  document.addEventListener('pointerenter', onPointerIntent, true);
  document.addEventListener('focusin', onPointerIntent, true);
  /* pointerdown prefetch — earlier than click, no pending UI that freezes hover */
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.button !== 0) return;
      const anchor = event.target.closest?.('a[href]');
      if (!isInternalNavAnchor(anchor)) return;
      prefetchUrl(anchor.href);
    },
    true
  );

  const warmNav = () => {
    document.querySelectorAll('.header a[href], #menu-drawer a[href]').forEach((a) => {
      if (isInternalNavAnchor(a)) prefetchUrl(a.href);
    });
  };

  /* Defer nav prefetch so hero video / first paint win the network */
  const scheduleWarmNav = () => {
    const start = () => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(warmNav, { timeout: 4000 });
      } else {
        warmNav();
      }
    };
    window.setTimeout(start, 1800);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleWarmNav, { once: true });
  } else {
    scheduleWarmNav();
  }

  // Clear any leftover progress DOM / session flag from older builds
  try {
    sessionStorage.removeItem('ac-nav-pending');
  } catch (e) {}
  document.documentElement.classList.remove('ac-nav-pending');
  document.querySelectorAll('a.is-nav-pending').forEach((el) => el.classList.remove('is-nav-pending'));
  document.getElementById('AcNavProgress')?.remove();

  function mobilePageScroll() {
    if (!window.matchMedia('(max-width: 989px)').matches) return null;
    const el = document.getElementById('AcPageScroll');
    if (!el) return null;
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll') return null;
    return el;
  }

  window.acPageScrollTop = function acPageScrollTop() {
    const el = mobilePageScroll();
    if (el) return el.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  };

  window.acSetPageScrollTop = function acSetPageScrollTop(y) {
    const el = mobilePageScroll();
    if (el) {
      el.scrollTop = y;
      return;
    }
    window.scrollTo(0, y);
  };

  const SCROLL_KEY = 'ac-page-scroll-map';

  function readScrollMap() {
    try {
      return JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function savePageScroll() {
    const el = mobilePageScroll();
    if (!el) return;
    const map = readScrollMap();
    map[location.pathname + location.search] = el.scrollTop;
    const keys = Object.keys(map);
    while (keys.length > 30) delete map[keys.shift()];
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  function restorePageScroll() {
    const el = mobilePageScroll();
    if (!el) return;
    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward') return;
    const y = readScrollMap()[location.pathname + location.search];
    if (typeof y === 'number') el.scrollTop = y;
  }

  window.addEventListener('pagehide', savePageScroll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restorePageScroll, { once: true });
  } else {
    restorePageScroll();
  }

  /* A phone tap was scrolling the outgoing page (menu clipped) before the
     next document loaded. Follow the link ourselves and keep the scroller put. */
  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target.closest?.('a[href]');
      /* Pattern circles swap the product in place. Following the link reloads
         the phone page and slides the chosen circle to the start of the row. */
      if (anchor?.closest('ac-pattern-picker')) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        return;
      }
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const el = mobilePageScroll();
      if (!el) return;
      if (!isInternalNavAnchor(anchor)) return;
      const top = el.scrollTop;
      event.preventDefault();
      el.scrollTop = top;
      if (anchor.classList.contains('ac-back')) {
        anchor.classList.add('is-loading');
        anchor.setAttribute('aria-busy', 'true');
        const spinner = anchor.querySelector('.ac-back__spinner');
        if (spinner) spinner.removeAttribute('hidden');
        const href = anchor.href;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.location.assign(href);
          });
        });
        return;
      }
      window.location.assign(anchor.href);
    },
    true
  );

  /* The document is locked, so the browser's pull-to-refresh never starts.
     Recreate it on the content scroller without moving the menu. */
  (function bindPullRefresh() {
    const THRESHOLD = 72;
    let startX = 0;
    let startY = 0;
    let pulling = false;
    let axisLocked = false;
    let delta = 0;
    let indicator = null;

    function pullIndicator() {
      if (indicator) return indicator;
      indicator = document.createElement('div');
      indicator.className = 'ac-pull-refresh';
      indicator.setAttribute('aria-hidden', 'true');
      const spin = document.createElement('span');
      spin.className = 'ac-pull-refresh__spin';
      indicator.appendChild(spin);
      document.body.appendChild(indicator);
      return indicator;
    }

    function blocksRefresh(target) {
      if (!target || !target.closest) return false;
      if (target.closest('.product__modal-opener--image.is-zoomed')) return true;
      if (target.closest('.product__media-icon')) return true;
      return false;
    }

    function setPull(amount) {
      const el = mobilePageScroll();
      const mark = pullIndicator();
      const distance = Math.max(0, amount);
      if (el) el.style.transform = distance ? `translate3d(0, ${distance}px, 0)` : '';
      mark.style.height = distance ? `${distance}px` : '0px';
      mark.classList.toggle('is-pulling', distance > 0 && !mark.classList.contains('is-refreshing'));
      mark.classList.toggle('is-ready', distance >= THRESHOLD);
    }

    document.addEventListener(
      'touchstart',
      (event) => {
        const el = mobilePageScroll();
        const mark = document.querySelector('.ac-pull-refresh');
        if (!el || event.touches.length !== 1 || el.scrollTop > 1) return;
        if (mark && mark.classList.contains('is-refreshing')) return;
        if (document.documentElement.classList.contains('overflow-hidden-mobile')) return;
        if (blocksRefresh(event.target)) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        pulling = true;
        axisLocked = false;
        delta = 0;
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      (event) => {
        if (!pulling) return;
        if (blocksRefresh(event.target)) {
          pulling = false;
          delta = 0;
          setPull(0);
          return;
        }
        const el = mobilePageScroll();
        if (!el) return;
        const dx = event.touches[0].clientX - startX;
        const dy = event.touches[0].clientY - startY;
        if (!axisLocked) {
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
            pulling = false;
            return;
          }
          if (dy < 6 || el.scrollTop > 1) return;
          axisLocked = true;
        }
        if (dy <= 0) {
          delta = 0;
          setPull(0);
          return;
        }
        delta = Math.min(dy * 0.45, 96);
        if (event.cancelable) event.preventDefault();
        setPull(delta);
      },
      { passive: false }
    );

    const endPull = () => {
      if (!pulling && delta === 0) return;
      const reload = delta >= THRESHOLD;
      pulling = false;
      axisLocked = false;
      if (reload) {
        const mark = pullIndicator();
        mark.classList.remove('is-pulling');
        mark.classList.add('is-ready', 'is-refreshing');
        delta = 0;
        window.location.reload();
        return;
      }
      delta = 0;
      setPull(0);
    };

    document.addEventListener('touchend', endPull);
    document.addEventListener('touchcancel', endPull);
  })();
})();
